import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { AdminUser } from '@baex/shared';
import { admins as adminsApi } from '../../api/endpoints';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { useBanners } from '../../components/ui/Banners';
import {
  EmptyState,
  PlainHeader,
  SkeletonRows,
  SortableHeader,
  Table,
  compareValues,
  nextSort,
} from '../../components/ui/Table';
import type { SortState } from '../../components/ui/Table';
import { useTimeFormat } from '../../lib/timezone';
import { ConfirmModal } from '../clients/ConfirmModal';
import { AddAdminModal } from './AddAdminModal';
import { ResetPasswordModal } from './ResetPasswordModal';

type SortKey = 'email' | 'displayName' | 'status' | 'addedAt';

const COLUMN_COUNT = 6;

/**
 * The "Admin Users — table and controls" section's safety rule: there must always be at least one way in. Disable and Remove
 * for the last active admin are subdued and `aria-disabled`, but stay pressable and
 * keyboard-reachable — pressing either opens the dialog below rather than refusing in
 * silence. This short line is only the button's `title`; the dialog does the explaining.
 */
export const LAST_ADMIN_REASON =
  "You can't disable or remove the last active admin — there would be no way back in.";

export const LAST_ADMIN_DIALOG_TITLE = 'This is the last active admin';

export const LAST_ADMIN_DIALOG_BODY =
  'is the only admin who can still sign in. Disabling or removing them would lock everyone ' +
  'out of this panel, and getting back in would mean running the seed command on the server.';

export const LAST_ADMIN_DIALOG_NEXT_STEP = 'Add a second admin first, then come back.';

export function isLastActiveAdmin(list: AdminUser[], candidate: AdminUser): boolean {
  if (candidate.status !== 'active') return false;
  return list.filter((admin) => admin.status === 'active').length <= 1;
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const banners = useBanners();
  const time = useTimeFormat();

  // Default sort: ADDED, oldest first, so the original admins stay at the top.
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'addedAt', direction: 'asc' });
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  /**
   * The admin whose Disable/Remove was pressed while they were the last one who can sign
   * in. Same convention as the Clients page's "still active" dialog: what is blocked, why,
   * and the button that unblocks it — here, opening Add Admin User directly (see the "Admin Users — table and controls" section).
   */
  const [lastAdminBlocked, setLastAdminBlocked] = useState<AdminUser | null>(null);

  const list = useQuery<AdminUser[]>({ queryKey: ['admins'], queryFn: () => adminsApi.list() });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admins'] });

  const setStatus = useMutation({
    mutationFn: (input: { email: string; status: 'active' | 'disabled' }) =>
      adminsApi.setStatus(input.email, input.status),
    onSuccess: async (_result, input) => {
      await invalidate();
      banners.show(`${input.email} ${input.status === 'disabled' ? 'disabled' : 'enabled'}.`);
    },
  });

  const data = useMemo(() => list.data ?? [], [list.data]);

  const rows = useMemo(() => {
    const pick = (admin: AdminUser): string | number | null => admin[sort.key];
    const sorted = [...data].sort((a, b) => compareValues(pick(a), pick(b)));
    return sort.direction === 'asc' ? sorted : sorted.reverse();
  }, [data, sort]);

  function onSort(key: SortKey) {
    setSort((current) => nextSort(current, key));
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-between">
        <h1 className="text-[15px] font-semibold text-ink">Admin Users</h1>
        <Button
          variant="primary"
          onClick={() => setAddOpen(true)}
          icon={<Plus size={14} aria-hidden="true" />}
        >
          Add Admin User
        </Button>
      </div>

      {list.isError ? (
        <ErrorNotice error={list.error} fallback="The admin list could not be loaded." />
      ) : null}
      {setStatus.isError ? <ErrorNotice error={setStatus.error} /> : null}

      <Table className="min-h-[8rem]">
        <thead>
          <tr>
            <SortableHeader label="Email" sortKey="email" sort={sort} onSort={onSort} />
            <SortableHeader label="Name" sortKey="displayName" sort={sort} onSort={onSort} />
            <SortableHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
            <SortableHeader
              label="Added"
              sortKey="addedAt"
              sort={sort}
              onSort={onSort}
              align="right"
              className="text-right"
            />
            <PlainHeader align="right">Last sign-in</PlainHeader>
            <PlainHeader align="right">Actions</PlainHeader>
          </tr>
        </thead>
        <tbody>
          {list.isLoading ? <SkeletonRows columns={COLUMN_COUNT} /> : null}

          {!list.isLoading && rows.length === 0 && !list.isError ? (
            <EmptyState
              title="No admin users yet"
              hint='Use "Add Admin User" to give someone access to this console.'
              columns={COLUMN_COUNT}
            />
          ) : null}

          {rows.map((admin) => {
            const locked = isLastActiveAdmin(data, admin);
            return (
              <tr key={admin.email} className="border-b border-hairline last:border-0">
                <td className="tabular px-3 py-1.5 text-ink">{admin.email}</td>
                <td className="max-w-[16rem] truncate px-3 py-1.5 text-ink-muted">
                  {admin.displayName}
                </td>
                <td className="px-3 py-1.5 text-ink-muted">{admin.status}</td>
                <td className="tabular px-3 py-1.5 text-right text-ink-muted">
                  {time.formatDay(admin.addedAt)}
                </td>
                <td
                  className="tabular px-3 py-1.5 text-right text-ink-muted"
                  title={time.rawTimestampTitle(admin.lastLoginAt)}
                >
                  {admin.lastLoginAt ? time.formatTimestamp(admin.lastLoginAt) : 'never'}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" onClick={() => setResetting(admin)}>
                      Reset password
                    </Button>
                    {admin.status === 'active' ? (
                      <Button
                        size="sm"
                        unavailable={
                          locked
                            ? {
                                reason: LAST_ADMIN_REASON,
                                onExplain: () => setLastAdminBlocked(admin),
                              }
                            : undefined
                        }
                        onClick={() => setStatus.mutate({ email: admin.email, status: 'disabled' })}
                      >
                        Disable
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setStatus.mutate({ email: admin.email, status: 'active' })}
                      >
                        Enable
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      unavailable={
                        locked
                          ? {
                              reason: LAST_ADMIN_REASON,
                              onExplain: () => setLastAdminBlocked(admin),
                            }
                          : undefined
                      }
                      onClick={() => setRemoving(admin)}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/*
        The "Admin Users — table and controls" section's "last-admin dialog": a dialog rather than a tooltip, because this
        obstacle has an obvious next step and a tooltip could not offer it. Deliberately
        the same shape as the Clients page's "still active" dialog — what is blocked, why,
        and the button that unblocks it — so the panel explains itself one way, not two.
      */}
      <ConfirmModal
        open={lastAdminBlocked !== null}
        title={LAST_ADMIN_DIALOG_TITLE}
        body={
          <>
            <span className="tabular">{lastAdminBlocked?.email ?? ''}</span>{' '}
            {LAST_ADMIN_DIALOG_BODY}
            <span className="mt-2 block">{LAST_ADMIN_DIALOG_NEXT_STEP}</span>
          </>
        }
        confirmLabel="Add an admin"
        onCancel={() => setLastAdminBlocked(null)}
        onConfirm={async () => {
          // Put the reader where they need to be rather than telling them where to go.
          setLastAdminBlocked(null);
          setAddOpen(true);
        }}
      />

      <AddAdminModal
        open={addOpen}
        existing={data}
        onCancel={() => setAddOpen(false)}
        onAdd={async (input) => {
          await adminsApi.create(input);
          await invalidate();
          setAddOpen(false);
          banners.show(`${input.email} added as an admin.`);
        }}
      />

      <ResetPasswordModal
        admin={resetting}
        onCancel={() => setResetting(null)}
        onSubmit={async (email, password) => {
          await adminsApi.setPassword(email, password);
          setResetting(null);
          banners.show(`Password reset for ${email}.`);
        }}
      />

      <ConfirmModal
        open={removing !== null}
        title="Remove Admin User"
        body={`Remove ${removing?.email ?? ''} as an admin? They will lose dashboard access immediately. This can be undone by adding them again.`}
        confirmLabel="Remove"
        busyLabel="Removing…"
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await adminsApi.remove(removing.email);
          await invalidate();
          banners.show(`${removing.email} removed.`);
          setRemoving(null);
        }}
      />
    </div>
  );
}
